import { format } from "date-fns";
import React from "react";

import { Chip, Link, Text } from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";

import ScrollableTable from "../../../../components/TableLite/TableLiteScrollable";
import MeetingCalendarIcon from "./MeetingCalendarIcon";
import MeetingRowOptionsDropdown from "./MeetingRowOptionsDropdown";

import type { MeetingTableRow } from "../types";

import EmptySearchTableMessage from "../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import { MEETING_STATUS_CONFIG, config } from "../config";

type MeetingsTableProps = {
	meetings: MeetingModelJson[];
	isFetchingMeetings: boolean | undefined;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	userId: string;
	onEditMeeting: (meeting: MeetingModelJson) => void;
	workspaceId: string;
};

const DataTable = (props: MeetingsTableProps): React.ReactElement => {
	const {
		meetings,
		isFetchingMeetings,
		activeSort,
		activeSortDirection,
		handleColumnHeaderClick,
		userId,
		onEditMeeting,
		workspaceId,
	} = props;

	const getStatusChip = (status: MeetingModelJson["status"]): React.ReactElement => {
		const { label, color } = MEETING_STATUS_CONFIG[status] || MEETING_STATUS_CONFIG.active;
		return <Chip label={label} color={color} size={24} />;
	};

	const formatMeetingDateTime = (startDateTime: Date, endDateTime: Date): React.ReactElement => {
		const isSameDay = startDateTime.toDateString() === endDateTime.toDateString();
		const formatTime = (date: Date): string => format(date, "h:mmaaa");
		const formatDateWithTime = (date: Date): string => format(date, "MMMM d, yyyy • h:mmaaa");

		if (isSameDay) {
			// Format: "January 9, 2026 • 1:00pm–3:00pm"
			const dateStr = format(startDateTime, "MMMM d, yyyy");
			const timeRange = `${formatTime(startDateTime)}–${formatTime(endDateTime)}`;

			return (
				<Text size="paragraph-3" weight="regular">
					{dateStr} • {timeRange}
				</Text>
			);
		}

		// Format: "January 9, 2026 • 12:00am–January 10, 2026 • 12:00am"
		return (
			<Text size="paragraph-3" weight="regular">
				{formatDateWithTime(startDateTime)}–{formatDateWithTime(endDateTime)}
			</Text>
		);
	};

	const columns = [
		{
			id: "name",
			label: "Meeting Name",
			isSortable: true,
			width: config.columns.name.width,
			getter: (meeting: MeetingTableRow): React.ReactElement => {
				const href = `/meeting-workspaces/${workspaceId}/meetings/${meeting._id}`;
				return (
					<div className="flex items-center space-x-4">
						<MeetingCalendarIcon meeting={meeting} />
						<Link href={href} className="no-underline hover:underline">
							<Text
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-line-clamp-1 max-w-[488px]"
							>
								{meeting.name}
							</Text>
						</Link>
					</div>
				);
			},
		},
		{
			id: "startDateTime",
			label: "Meeting Date",
			isSortable: true,
			width: config.columns.meetingDate.width,
			getter: (meeting: MeetingTableRow): React.ReactElement =>
				formatMeetingDateTime(meeting.startDateTime, meeting.endDateTime),
		},
		{
			id: "agendaItems",
			label: "Agenda Items",
			isSortable: true,
			width: config.columns.agendaItems.width,
			getter: (meeting: MeetingTableRow): React.ReactElement => (
				<div className="flex w-full justify-center">
					<Text size="paragraph-3" weight="regular">
						{meeting.agendaItemCount ?? 0}
					</Text>
				</div>
			),
		},
		{
			id: "actionItems",
			label: "Action Items",
			isSortable: true,
			width: config.columns.actionItems.width,
			getter: (meeting: MeetingTableRow): React.ReactElement => (
				<div className="flex w-full justify-center">
					<Text size="paragraph-3" weight="regular">
						{meeting.actionItemCount ?? 0}
					</Text>
				</div>
			),
		},
		{
			id: "status",
			label: "Status",
			isSortable: true,
			width: config.columns.status.width,
			getter: (meeting: MeetingTableRow): React.ReactElement => getStatusChip(meeting.status),
		},
		{
			id: "control",
			label: "",
			isSortable: false,
			width: config.columns.control.width,
			getter: (meeting: MeetingTableRow): React.ReactElement => (
				<MeetingRowOptionsDropdown meeting={meeting} userId={userId} onEditMeeting={onEditMeeting} />
			),
		},
	];

	return (
		<div data-testid="meetingsTableContainer">
			<ScrollableTable
				rowKey="_id"
				columns={columns}
				isLoading={isFetchingMeetings ?? false}
				rows={meetings as MeetingTableRow[]}
				activeSort={activeSort}
				activeSortDirection={activeSortDirection}
				onColumnClick={(columnID, nextDirection) =>
					handleColumnHeaderClick({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
				emptyChildren={<EmptySearchTableMessage mainText="No Meetings Found" />}
			/>
		</div>
	);
};

export default DataTable;
